import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { Ticket, RefreshCw, Settings as SettingsIcon, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const DAY_PRESETS = [
  { id: 1, label: "Yesterday" },
  { id: 7, label: "Last 7 days" },
  { id: 14, label: "Last 14 days" },
  { id: 30, label: "Last 30 days" },
  { id: 60, label: "Last 60 days" },
  { id: 90, label: "Last 90 days" },
];

export default function TicketSalesBlock({ block, isTeam, onUpdateBlock }) {
  const { id: clientId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  const cfg = block.config || {};
  const days = cfg.days || 30;
  const viewMode = cfg.view_mode || "chart"; // chart | table | summary
  const goal = parseFloat(cfg.daily_goal || 0);
  const pricePerTicket = parseFloat(cfg.price_per_ticket || 0);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/clients/${clientId}/ticket_sales?days=${days}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load ticket sales");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, days]);

  // Realtime refresh when a checkin gets saved for this client
  useEffect(() => {
    const onCheckin = (e) => {
      if (e.detail?.clientId === clientId && e.detail?.fieldType === "ticket_sales") {
        load();
      }
    };
    window.addEventListener("tt-checkin-updated", onCheckin);
    return () => window.removeEventListener("tt-checkin-updated", onCheckin);
  /* eslint-disable-next-line */
  }, [clientId, days]);

  const maxVal = useMemo(() => Math.max(1, ...(data?.series || []).map((s) => s.tickets)), [data]);
  const totalRevenue = data && pricePerTicket > 0 ? data.total * pricePerTicket : null;

  return (
    <div className="tt-card p-5 anim-fade-up" data-testid="ticket-sales-block">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Ticket className="w-3.5 h-3.5 text-zinc-300" />
          <span className="t-eyebrow">Ticket Sales · {cfg.title || (days === 1 ? "Yesterday" : `Last ${days} days`)}</span>
        </div>
        <div className="flex items-center gap-2">
          {isTeam && (
            <>
              <select value={days} onChange={(e) => onUpdateBlock(block.id, { config: { ...cfg, days: parseInt(e.target.value) } })}
                className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-8 text-[11px] px-2"
                data-testid="ticket-sales-range">
                {DAY_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <select value={viewMode} onChange={(e) => onUpdateBlock(block.id, { config: { ...cfg, view_mode: e.target.value } })}
                className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-8 text-[11px] px-2"
                data-testid="ticket-sales-view">
                <option value="chart">Chart</option>
                <option value="table">Table</option>
                <option value="summary">Summary</option>
              </select>
              <Button size="sm" variant="ghost" onClick={() => setShowSettings((v) => !v)} className="h-8 text-[11px]" data-testid="ticket-sales-settings-btn"><SettingsIcon className="w-3 h-3" /></Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={load} className="h-8 text-[11px]" data-testid="ticket-sales-refresh"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {showSettings && isTeam && (
        <TicketSalesSettings cfg={cfg}
          onSave={(patch) => { onUpdateBlock(block.id, { config: { ...cfg, ...patch } }); setShowSettings(false); }}
          onClose={() => setShowSettings(false)} />
      )}

      {err ? (
        <div className="flex items-start gap-2 text-[12px] text-[#FCA5A5] flag-red rounded-md p-3" data-testid="ticket-sales-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>{err}</div>
        </div>
      ) : loading && !data ? (
        <div className="text-zinc-600 text-[12px]">Loading…</div>
      ) : !data || data.fields.length === 0 ? (
        <div className="text-zinc-600 text-[12px]">No ticket-sales fields configured for this client. Add one in Client Settings → Check-in fields with type <em>ticket sales</em>.</div>
      ) : (
        <>
          {/* KPI strip — always visible */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="ticket-sales-kpis">
            <Kpi label="Yesterday" value={(data.today ?? 0).toLocaleString()} hint="tickets sold yesterday" highlight />
            <Kpi label="Total" value={data.total.toLocaleString()} hint={`over ${data.days}d`} />
            <Kpi label="Avg / day" value={data.avg_per_day.toLocaleString()} hint="across all sources" />
            {totalRevenue != null ? (
              <Kpi label="Revenue" value={`₹${totalRevenue.toLocaleString()}`} hint={`@ ₹${pricePerTicket}/ticket`} />
            ) : (
              <Kpi label="Best day" value={data.best_day ? data.best_day.tickets.toLocaleString() : "—"} hint={data.best_day ? data.best_day.date.slice(5) : ""} />
            )}
          </div>

          {viewMode === "summary" ? null : viewMode === "table" ? (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-[12px] min-w-[420px]">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    {data.fields.map((f) => <th key={f.id} className="text-right p-2">{f.label}</th>)}
                    <th className="text-right p-2">Total</th>
                    {pricePerTicket > 0 && <th className="text-right p-2">Revenue</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...data.series].reverse().map((s) => (
                    <tr key={s.date} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                      <td className="p-2 font-mono text-zinc-400">{s.date}</td>
                      {data.fields.map((f) => <td key={f.id} className="p-2 text-right font-mono text-zinc-200">{(s.by_field?.[f.id] || 0).toLocaleString()}</td>)}
                      <td className="p-2 text-right font-mono text-zinc-100 font-semibold">{s.tickets.toLocaleString()}</td>
                      {pricePerTicket > 0 && <td className="p-2 text-right font-mono text-zinc-200">₹{(s.tickets * pricePerTicket).toLocaleString()}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-end gap-1 h-32" data-testid="ticket-sales-chart">
              {data.series.map((s) => {
                const pct = (s.tickets / maxVal) * 100;
                const overGoal = goal > 0 && s.tickets >= goal;
                return (
                  <div key={s.date} className="flex-1 flex flex-col items-center gap-1 group min-w-[6px]">
                    <span className="text-[8px] text-zinc-600 font-mono opacity-0 group-hover:opacity-100">{s.tickets}</span>
                    <div className="w-full rounded-t-sm transition-all" style={{
                      height: `${pct}%`,
                      background: s.tickets === 0 ? "rgba(255,255,255,0.08)" : overGoal ? "#34D399" : pct > 60 ? "#FAFAFA" : "rgba(255,255,255,0.55)",
                    }} />
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3 text-[10px] text-zinc-700 font-mono uppercase tracking-wider">
            Fields tracked: {data.fields.map((f) => f.label).join(" · ") || "—"}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, highlight }) {
  return (
    <div className={`border rounded-md p-2.5 ${highlight ? "bg-white text-black border-white" : "bg-[#0a0a0a] border-zinc-800"}`}>
      <div className={`t-eyebrow text-[9px] ${highlight ? "text-black/60" : ""}`}>{label}</div>
      <div className={`font-display text-[18px] font-semibold mt-0.5 ${highlight ? "text-black" : ""}`}>{value}</div>
      <div className={`text-[10px] font-mono truncate ${highlight ? "text-black/50" : "text-zinc-600"}`}>{hint}</div>
    </div>
  );
}

function TicketSalesSettings({ cfg, onSave, onClose }) {
  const [title, setTitle] = useState(cfg.title || "");
  const [goal, setGoal] = useState(cfg.daily_goal || "");
  const [price, setPrice] = useState(cfg.price_per_ticket || "");

  return (
    <div className="tt-card p-4 mb-4 space-y-3 anim-fade-up" data-testid="ticket-sales-settings">
      <div>
        <div className="t-eyebrow mb-1.5">Title</div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="t-eyebrow mb-1.5">Daily goal (tickets)</div>
          <Input type="number" value={goal} onChange={(e) => setGoal(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] font-mono" placeholder="0 = none" />
        </div>
        <div>
          <div className="t-eyebrow mb-1.5">Price / ticket (₹)</div>
          <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] font-mono" placeholder="0 = hide revenue" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onClose} className="h-8 text-[11px]">Cancel</Button>
        <Button size="sm" onClick={() => onSave({ title, daily_goal: goal, price_per_ticket: price })}
          className="bg-white text-black hover:bg-zinc-200 h-8 text-[11px]">Save</Button>
      </div>
    </div>
  );
}
